import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useVideoPlayer, type VideoPlayer } from 'expo-video';
import { reaction } from 'mobx';

import type { IGuidedWorkoutPlayerStore } from '@/stores/guidedWorkoutPlayer/GuidedWorkoutPlayerStore';
import { subscribeAutoPlay } from './playerUtils';

/**
 * Controller hook for guided (chaptered) workouts.
 *
 * Owns the expo-video player instance and all playback wiring:
 * - Initializes the player with store.playbackVideoUri (set before mount)
 * - Auto-plays when player becomes ready
 * - Forwards timeUpdate events to the store
 * - Forwards playToEnd events to the store
 * - Syncs play/pause with store.isPaused
 * - Seeks the player when store.pendingSeekMs is set (skip/restart chapter)
 */
export function useGuidedPlayerController(store: IGuidedWorkoutPlayerStore): {
  player: VideoPlayer;
} {
  const player = useVideoPlayer(null, (p) => {
    p.loop = false;
    p.timeUpdateEventInterval = 1;
    p.muted = true;
  });

  useEffect(() => {
    if (!store.playbackVideoUri) return;
    let cancelled = false;
    const id = setTimeout(() => {
      if (cancelled) {
        console.log(
          '[GuidedController] Initialization cancelled, skipping player source setup',
        );
        return;
      }
      // On web, encode the resume start time as a media fragment URI (#t=N).
      // The browser's HTML5 video element honors this and starts buffering
      // from that position natively — far cleaner than a post-load seek,
      // which on web triggers a loading→ready buffer flap and a visible
      // stutter. Native (expo-video on iOS/Android) gets the bare URI and
      // we use the seek-floor approach instead.
      let sourceUri: string = store.playbackVideoUri!;
      if (Platform.OS === 'web') {
        const floorMs = store.initialSeekMs;
        if (floorMs != null && floorMs > 0) {
          const sep = sourceUri.includes('#') ? '&' : '#';
          sourceUri = `${sourceUri}${sep}t=${(floorMs / 1000).toFixed(2)}`;
          console.log(
            '[GuidedController] using media fragment URI for resume',
            sourceUri,
          );
        }
      }
      console.log('[GuidedController] Setting player source to', sourceUri);
      player.replaceAsync(sourceUri).catch((err) => {
        console.warn('[GuidedController] replaceAsync failed', err);
      });
    }, 1000);

    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [player, store.playbackVideoUri]);

  // Native pre-play seek to resume floor. Runs on the first readyToPlay
  // before subscribeAutoPlay's listener fires, so play() picks up from the
  // floor instead of 0. Web uses the #t media fragment URI above instead.
  const seekedRef = useRef(false);
  useEffect(() => {
    if (Platform.OS === 'web') return;
    seekedRef.current = false;
    const sub = player.addListener('statusChange', (payload) => {
      if (payload.status !== 'readyToPlay') return;
      if (seekedRef.current) return;
      const floorMs = store.initialSeekMs;
      if (floorMs != null && floorMs > 0) {
        const currentMs = (player.currentTime ?? 0) * 1000;
        if (currentMs < floorMs - 1) {
          console.log(
            '[GuidedController] pre-play seek',
            'from',
            currentMs,
            'to',
            floorMs,
          );
          player.currentTime = floorMs / 1000;
        }
      }
      seekedRef.current = true;
    });
    return () => sub.remove();
  }, [player, store]);

  // Auto-play when player becomes ready (1s delay on web for guided).
  // Restored to the original simple behavior so brand-new workouts behave
  // the same as before any resume-related changes.
  useEffect(() => {
    const sub = subscribeAutoPlay(player, () => store.isPaused, 1000);
    return () => sub.remove();
  }, [player, store]);

  // Forward timeUpdate to store.
  // When resuming a workout, store.initialSeekMs acts as a floor: if the
  // player reports a position below it, force the player forward and report
  // the floored time. We never auto-clear the floor — once playback is
  // reliably past it the check is a no-op, and the floor also protects
  // against transient buffer-reset events that re-emit currentTime=0.
  // Matches mobile (videoPlayerModel.ts:218-226).
  useEffect(() => {
    const sub = player.addListener('timeUpdate', (event) => {
      let currentMs = event.currentTime * 1000;
      const floorMs = store.initialSeekMs;
      if (floorMs != null && currentMs < floorMs) {
        console.log(
          '[GuidedController] timeUpdate floor override',
          'currentMs=',
          currentMs,
          'floorMs=',
          floorMs,
        );
        player.currentTime = floorMs / 1000;
        currentMs = floorMs;
      }
      store.onTimeUpdate(currentMs);
    });
    return () => sub.remove();
  }, [player, store]);

  // Forward playToEnd to store
  useEffect(() => {
    const sub = player.addListener('playToEnd', () => {
      console.log(
        '[GuidedController] Video playToEnd',
        'status=',
        player.status,
        'currentTime=',
        player.currentTime,
        'duration=',
        player.duration,
      );
      // Guard against spurious playToEnd during source init when the playbackUri is initially null and then
      // replaced with the real video.
      // If we ever change the approach of starting as null, we can probably remove this guard.
      if (player.duration > 0 && player.currentTime >= player.duration - 0.5) {
        store.onVideoEnd();
      }
    });
    return () => sub.remove();
  }, [player, store]);

  // Diagnostic: log every status change with error details
  useEffect(() => {
    const sub = player.addListener('statusChange', (payload) => {
      console.log(
        '[GuidedController] statusChange',
        'status=',
        payload.status,
        'oldStatus=',
        payload.oldStatus,
        'error=',
        payload.error,
      );
    });
    return () => sub.remove();
  }, [player, store]);

  // Pause/play sync
  useEffect(() => {
    const dispose = reaction(
      () => store.isPaused,
      (isPaused) => {
        if (isPaused) {
          player.pause();
        } else {
          // Always unmute on unpause; play() is gated on readiness because
          // subscribeAutoPlay will kick off playback once the source loads.
          player.muted = false;
          if (player.status === 'readyToPlay') {
            console.log('[GuidedController] Resuming playback');
            player.play();
          }
        }
      },
    );
    return dispose;
  }, [player, store]);

  // Mirror the player's real play/pause state back into the store.
  //
  // ANDROID ONLY. expo-video attaches a Media3 MediaSession to the ExoPlayer, and
  // the OS routes the hardware play/pause button straight to it — toggling
  // playback without going through the store. We observe that and sync
  // `isPaused` so the on-screen icon and pause logic follow. iOS/tvOS have no such
  // session and drive the toggle through `useTVTransportControls` instead.
  //
  // The catch on Android: expo-video only exposes `isPlaying`, which is false for
  // BOTH a genuine pause AND a transient seek/buffer stall (skip/restart seeks).
  // Naively mirroring every `isPlaying=false` latches a pause mid-seek; combined
  // with the `isPaused`→player reaction below and `subscribeAutoPlay` re-playing on
  // each `readyToPlay`, that produced a play/pause/play/pause oscillation after
  // fast-forward (`status` and `isPlaying` cross the bridge non-atomically, so a
  // plain `status === 'readyToPlay'` guard leaks).
  //
  // So: a resume (`isPlaying=true`) is unambiguous and mirrors immediately, but a
  // pause is only mirrored after a short confirm window during which the player
  // stays `readyToPlay` AND not `playing`. A real pause persists; a seek/buffer
  // moves on (and its resume event cancels the pending confirm).
  useEffect(() => {
    if (Platform.OS !== 'android') return;
    let confirmTimer: ReturnType<typeof setTimeout> | null = null;
    const clearConfirm = () => {
      if (confirmTimer) {
        clearTimeout(confirmTimer);
        confirmTimer = null;
      }
    };

    const sub = player.addListener('playingChange', ({ isPlaying }) => {
      clearConfirm();
      if (isPlaying) {
        store.setPaused(false);
        return;
      }
      confirmTimer = setTimeout(() => {
        confirmTimer = null;
        if (player.status === 'readyToPlay' && !player.playing) {
          store.setPaused(true);
        }
      }, 600);
    });

    return () => {
      clearConfirm();
      sub.remove();
    };
  }, [player, store]);

  // Seek on demand: react to pendingSeekMs from skip/restart chapter
  useEffect(() => {
    const dispose = reaction(
      () => store.pendingSeekMs,
      (seekMs) => {
        if (seekMs != null) {
          player.currentTime = seekMs / 1000;
          store.clearPendingSeek();
        }
      },
    );
    return dispose;
  }, [player, store]);

  return { player };
}
